import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/api/client";
import { CONTRACT_STATUS, CONTRACT_SOURCE, AI_STATUS, fmtDateTime } from "@/lib/domain";
import { Chip } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, FileText, Upload } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function Contracts() {
  const [contracts, setContracts] = useState([]);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const load = () => api.get("/contracts").then((r) => setContracts(r.data));
  useEffect(() => { load(); }, []);

  const startNew = () => { setTitle(""); setFile(null); setOpen(true); };

  const save = async () => {
    if (!title.trim()) {
      toast.error("Укажите название договора");
      return;
    }
    try {
      const res = await api.post("/contracts", { title: title.trim(), source: "crm_upload" });
      const contractId = res.data.id;
      if (file) {
        const form = new FormData();
        form.append("file", file);
        form.append("label", "Hauptvertrag");
        await api.post(`/contracts/${contractId}/files`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success("Договор создан");
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Ошибка: " + formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6">
      <Toaster theme="dark" position="top-right" />
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-amber-400/80 mb-2 font-medium">Grandmont Group</div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl">Договоры</h1>
          <p className="text-slate-400 mt-1 text-sm">{contracts.length} договоров</p>
        </div>
        <Button data-testid="new-contract-button" onClick={startNew} className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">
          <Plus size={16} /> Новый договор
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {contracts.length === 0 && (
          <Card className="col-span-full p-10 bg-[#131924] border-[#212b3d] text-center text-slate-500">
            <FileText className="mx-auto mb-3 opacity-50" size={32} />
            Договоров ещё нет.
          </Card>
        )}
        {contracts.map((c) => {
          const status = CONTRACT_STATUS[c.status] || { label: c.status, cls: "bg-slate-500/20 text-slate-300 border-slate-500/30" };
          const ai = AI_STATUS[c.ai_status];
          return (
            <Card
              key={c.id}
              data-testid={`contract-card-${c.id}`}
              onClick={() => navigate(`/contracts/${c.id}`)}
              className="p-5 bg-[#131924] border-[#212b3d] hover:border-amber-500/30 transition cursor-pointer group"
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0 flex-1">
                  <div className="font-display font-semibold text-lg text-slate-100 truncate">{c.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{CONTRACT_SOURCE[c.source] || c.source}</div>
                </div>
                <Chip className={status.cls}>{status.label}</Chip>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
                <FileText size={12} /> {(c.files || []).length} файл(ов)
                {ai && ai.label !== "—" && <Chip className={ai.cls}>{ai.label}</Chip>}
              </div>
              <div className="text-[11px] text-slate-600 pt-3 border-t border-[#212b3d]">
                Создан {fmtDateTime(c.created_at)}
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#131924] border-[#212b3d] text-slate-100 max-w-md">
          <DialogHeader><DialogTitle className="font-display">Новый договор</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Название *</Label>
              <Input data-testid="contract-title-input" value={title} onChange={(e) => setTitle(e.target.value)} className="bg-[#0f1521] border-[#212b3d]" />
            </div>
            <div>
              <Label>Файл договора (опционально)</Label>
              <input
                ref={fileInputRef}
                data-testid="contract-file-input"
                type="file"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-slate-400 mt-1 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-[#1c2434] file:text-slate-200 file:text-sm hover:file:bg-[#232d40]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="save-contract-button" onClick={save} className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">
              <Upload size={14} /> Создать
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
